@if (advisory(); as a) {
  <mat-card appearance="outlined" class="ai-card">
    <mat-card-content>
      <div class="ai-card__header">
        <h2 class="section-title">
          <span>AI Advisory</span>
          @if (!a.is_stub) {
            <app-ai-mark />
          }
        </h2>
        @if (canRun() && !runWait()) {
        <button
          mat-stroked-button
          type="button"
          (click)="assess.emit()"
          [disabled]="running()"
          data-testid="rerun-assess-btn"
        >
          <app-icon name="refresh" [size]="16" />
          <span>{{ running() ? 'Re-running…' : runLabel() || 'Re-run assessment' }}</span>
        </button>
        }
      </div>

      @if (a.is_stub) {
        <div class="sg-banner sg-banner--warning" role="status" data-testid="ai-advisory-stub">
          <app-icon name="alert-triangle" [size]="18" />
          <span>
            <strong>Not an AI assessment.</strong> The AI was switched off when this ran, so this is
            a placeholder answer. Don't rely on it.
          </span>
        </div>
      }

      <div class="ai-rec">
        <span class="ai-rec__label">Recommendation</span>
        <app-status-chip [status]="a.recommendation ?? 'UNKNOWN'" />
        @if (showConfidence() && a.confidence !== null) {
          <span class="ai-rec__confidence">{{ a.confidence }}% confidence</span>
        }
      </div>

      @if (showComparison()) {
        <div class="ai-compare" data-testid="ai-model-comparison">
          <div class="ai-compare__head">
            <p class="ai-compare__title">Model comparison</p>
            @if (agreed()) {
              <span class="ai-compare__agree ai-compare__agree--yes">
                <app-icon name="check" [size]="14" />
                <span>Models agree</span>
              </span>
            } @else {
              <span class="ai-compare__agree ai-compare__agree--no">
                <app-icon name="alert-triangle" [size]="14" />
                <span>Models disagree</span>
              </span>
            }
          </div>

          <table class="ai-compare__table">
            <thead>
              <tr>
                <th scope="col">Model</th>
                <th scope="col">Recommendation</th>
                @if (showConfidence()) {
                  <th scope="col" class="ai-compare__num">Confidence</th>
                }
                <th scope="col" class="ai-compare__num">Cost</th>
                <th scope="col" class="ai-compare__num">Time</th>
              </tr>
            </thead>
            <tbody>
              @for (m of perModel(); track m.model) {
                <tr>
                  <td>{{ m.model }}</td>
                  <td><app-status-chip [status]="m.recommendation ?? 'UNKNOWN'" /></td>
                  @if (showConfidence()) {
                    <td class="ai-compare__num">
                      {{ m.confidence !== null ? m.confidence + '%' : '—' }}
                    </td>
                  }
                  <td class="ai-compare__num">ZAR {{ m.cost_zar | number: '1.4-4' }}</td>
                  <td class="ai-compare__num">{{ m.latency_ms | number: '1.0-0' }} ms</td>
                </tr>
              }
            </tbody>
          </table>

          @if (costSpread(); as cost) {
            <p class="ai-compare__delta muted">
              Cheapest ZAR {{ cost.min | number: '1.4-4' }} · dearest ZAR
              {{ cost.max | number: '1.4-4' }}
              @if (cost.factor) {
                — {{ cost.factor | number: '1.1-1' }}× more for the dearest model
              }
            </p>
          }
        </div>
      }

      @if (showContradiction()) {
        <div class="ai-card__reconcile" role="note">
          <app-icon name="info" [size]="16" />
          <span>
            The Credit Committee has reviewed and approved this matter. This advisory does not bind
            the committee's resolution — it is provided for context only.
          </span>
        </div>
      }

      @if (a.summary) {
        <p class="ai-summary">{{ a.summary }}</p>
      }

      @if (a.risk_flags.length) {
        <div class="ai-flags ai-flags--risk">
          <p class="ai-flags__label">Risk flags</p>
          <ul class="ai-flags__list">
            @for (flag of a.risk_flags; track flag) {
              <li>{{ flag }}</li>
            }
          </ul>
        </div>
      }

      @if (a.missing_items.length) {
        <div class="ai-flags ai-flags--missing">
          <p class="ai-flags__label">Missing for a decision</p>
          <ul class="ai-flags__list">
            @for (item of a.missing_items; track item) {
              <li>{{ item }}</li>
            }
          </ul>
        </div>
      }

      <p class="muted ai-card__foot">
        Advisory only — the analyst's assessment and decision are recorded below.
        @if (a.assessed_at) {
          <span class="ai-card__stamp">Assessed {{ a.assessed_at | date: 'medium' }}.</span>
        }
      </p>
    </mat-card-content>
  </mat-card>
} @else {
  <mat-card appearance="outlined" class="ai-card ai-card--empty">
    <mat-card-content>
      <h2 class="section-title">
        <span>AI Advisory</span>
        <app-ai-mark />
      </h2>
      <div class="ai-empty">
        @if (running()) {
          <p class="ai-empty__title">The assessment is running</p>
          <p class="ai-empty__body">
            This takes a minute or two. The advisory recommendation will appear here when it
            finishes. It is advisory only; you make the final call.
          </p>
        } @else {
          <p class="ai-empty__title">No assessment has been run yet</p>
          <p class="ai-empty__body">
            Run an assessment to get an advisory recommendation — approve, decline, or more info
            needed — with the rationale, confidence, and any risk flags behind it. It is advisory
            only; you make the final call.
          </p>
        }
        @if (runWait(); as wait) {
          <p class="ai-empty__body" data-testid="run-assess-wait">{{ wait }}</p>
        } @else if (canRun()) {
        <button
          mat-flat-button
          color="primary"
          type="button"
          (click)="assess.emit()"
          [disabled]="running()"
          data-testid="run-assess-btn"
        >
          <app-icon name="sparkles" [size]="16" />
          <span>{{ running() ? 'Running assessment…' : runLabel() || 'Run assessment' }}</span>
        </button>
        }
      </div>
    </mat-card-content>
  </mat-card>
}
